package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin

/** Procedural stereo ambience generator. All per-frame state is primitive and preallocated. */
class LayeredAmbienceRenderer internal constructor(
    val sampleRate: Double,
    seed: ULong,
    scene: AmbienceScene,
    usePresetCombination: Boolean = false,
    materialCycle: SoundMaterialCycle? = null,
) : PcmRenderer {
    private val profile = SceneSoundProfile()
    private val random = SplitMix64(0uL)
    private val seeder = SplitMix64(0uL)
    private val fireTexture = FireTextureState(sampleRate)
    private val additionalTexture = AdditionalTextureState(sampleRate)
    private val eventA = EventVoice(sampleRate)
    private val eventB = EventVoice(sampleRate)
    private val droneImperfection = AnalogImperfectionState(sampleRate, 0uL)
    private val toneImperfection = AnalogImperfectionState(sampleRate, 0uL)
    private val motionControl = InterpolatedSineControl(sampleRate)
    private val wideControl = InterpolatedSineControl(sampleRate)
    private val droneOscillator = RecursiveSineOscillator(sampleRate)
    private val droneOvertoneOscillator = RecursiveSineOscillator(sampleRate)
    private val toneOscillator = RecursiveSineOscillator(sampleRate)
    private val tonePulseControl = InterpolatedSineControl(sampleRate)

    private var commonFast = 0f
    private var commonSlow = 0f
    private var commonDeep = 0f
    private var sideLeft = 0f
    private var sideRight = 0f
    private var previousNoise = 0f
    private var startupGain = 0f
    private var fireStereoWidth = 0f
    private var oscillatorControlCounter = 0
    private var visualControlCounter = 0
    private var lastDroneMotion = 0f
    private var lastTonePulse = 0f
    private var textureVisualEnergy = 0f
    private var surfVisualConvergence = 0f
    private var previousVisualMotion = 0f
    private val visualAttackCoefficient = (1.0 - exp(-1.0 / (0.040 * sampleRate))).toFloat()
    private val visualReleaseCoefficient = (1.0 - exp(-1.0 / (0.350 * sampleRate))).toFloat()

    private val textureVisual = MutableVisualSignal()
    private val droneVisual = MutableVisualSignal()
    private val toneVisual = MutableVisualSignal()
    private val eventAVisual = MutableVisualSignal()
    private val eventBVisual = MutableVisualSignal()

    internal var sampleLeft = 0f
        private set
    internal var sampleRight = 0f
        private set

    val matchesPresetCombination: Boolean
        get() = profile.matchesPresetCombination

    internal val debugTexture: TextureKind
        get() = profile.texture
    internal val debugEvent: EventKind
        get() = profile.event
    internal val debugSecondaryEvent: EventKind
        get() = profile.secondaryEvent

    init {
        require(sampleRate.isFinite() && sampleRate > 0.0) { "sampleRate must be finite and positive" }
        reset(seed, scene, usePresetCombination, materialCycle)
    }

    /** Called by the morph renderer only while this generator is inaudible. */
    fun reset(seed: ULong, scene: AmbienceScene, usePresetCombination: Boolean = false) {
        reset(seed, scene, usePresetCombination, null)
    }

    internal fun reset(
        seed: ULong,
        scene: AmbienceScene,
        usePresetCombination: Boolean = false,
        materialCycle: SoundMaterialCycle?,
    ) {
        profile.reconfigure(scene, seed, usePresetCombination, materialCycle)
        fireStereoWidth = if (seed and 1uL == 0uL) 0.28f else 0.02f
        seeder.reset(seed xor 0x94D0_49BB_1331_11EBuL)
        random.reset(seeder.nextULong())
        droneImperfection.reset(seed xor 0xDB4F_0B91_75AE_2165uL)
        toneImperfection.reset(seed xor 0xBBE0_5633_03A4_619FuL)

        commonFast = 0f
        commonSlow = 0f
        commonDeep = 0f
        sideLeft = 0f
        sideRight = 0f
        previousNoise = 0f
        fireTexture.reset()
        additionalTexture.reset(profile.texture, seed)
        startupGain = 0f
        lastDroneMotion = 0f
        lastTonePulse = 0f
        textureVisualEnergy = 0f
        surfVisualConvergence = 0f
        previousVisualMotion = 0f
        oscillatorControlCounter = 0
        visualControlCounter = 0
        textureVisual.clear()
        droneVisual.clear()
        toneVisual.clear()
        eventAVisual.clear()
        eventBVisual.clear()

        eventA.configure(
            profile.event,
            profile.eventLevel,
            profile.eventDelayLow,
            profile.eventDelayHigh,
            seeder.nextULong(),
        )
        eventB.configure(
            profile.secondaryEvent,
            profile.secondaryLevel,
            profile.secondaryDelayLow,
            profile.secondaryDelayHigh,
            seeder.nextULong(),
        )

        motionControl.reset(random.nextUnitDouble() * PI * 2.0, profile.motionRate)
        wideControl.reset(random.nextUnitDouble() * PI * 2.0, 0.013)
        droneOscillator.reset(random.nextUnitDouble() * PI * 2.0, profile.droneFrequency)
        droneOvertoneOscillator.reset(
            random.nextUnitDouble() * PI * 2.0,
            profile.droneFrequency * 2.01,
        )
        toneOscillator.reset(random.nextUnitDouble() * PI * 2.0, profile.toneFrequency)
        tonePulseControl.reset(random.nextUnitDouble() * PI * 2.0, profile.tonePulseRate)
        sampleLeft = 0f
        sampleRight = 0f
        updateVisualFrame(0f, 0f)
    }

    override fun render(interleaved: FloatArray, frameCount: Int) {
        require(frameCount >= 0 && interleaved.size >= frameCount * 2) {
            "interleaved buffer must contain two floats per frame"
        }
        var offset = 0
        repeat(frameCount) {
            nextRawStereoSample()
            interleaved[offset] = softLimit(sampleLeft)
            interleaved[offset + 1] = softLimit(sampleRight)
            offset += 2
        }
    }

    internal fun nextRawStereoSample() {
        val noise = random.nextBipolarFloat()
        val independentNoiseLeft = random.nextBipolarFloat()
        val independentNoiseRight = random.nextBipolarFloat()

        commonFast += 0.12f * (noise - commonFast)
        commonSlow += 0.012f * (commonFast - commonSlow)
        commonDeep += 0.0011f * (noise - commonDeep)
        sideLeft += 0.08f * (independentNoiseLeft - sideLeft)
        sideRight += 0.08f * (independentNoiseRight - sideRight)

        val motion = (motionControl.next() + 1f) * 0.5f
        val wander = wideControl.next() * 0.035f

        val texture = nextTextureSample(noise, motion)
        val body = commonSlow * (1f - profile.brightness) +
            (commonFast - commonSlow) * profile.brightness + commonDeep * 0.23f
        val bedCore = when (profile.texture) {
            TextureKind.Fire, TextureKind.BrownNoise -> texture
            TextureKind.Brook,
            TextureKind.Waterfall,
            TextureKind.Blizzard,
            TextureKind.Ventilation,
            TextureKind.DistantTraffic,
            TextureKind.BoatEngine,
            -> texture * 0.90f + body * 0.10f
            else -> body * 0.55f + texture * 0.45f
        }
        val bed = bedCore * profile.bedLevel
        var independentLeft = sideLeft * profile.bedLevel * 0.08f
        var independentRight = sideRight * profile.bedLevel * 0.08f
        if (profile.texture == TextureKind.Fire) {
            val fractureSide = fireTexture.sideActivity * profile.bedLevel * fireStereoWidth
            independentLeft += (independentNoiseLeft - sideLeft) * fractureSide
            independentRight += (independentNoiseRight - sideRight) * fractureSide
        }

        nextContinuousTone()
        val continuousLeft = sampleLeft
        val continuousRight = sampleRight
        eventA.nextSample(noise)
        eventB.nextSample(noise)
        updateVisualFrame(motion, texture)
        oscillatorControlCounter = (oscillatorControlCounter + 1) and
            (OSCILLATOR_CONTROL_INTERVAL_FRAMES - 1)
        visualControlCounter = (visualControlCounter + 1) and 63

        startupGain += 0.00009f * (1f - startupGain)
        val left = bed * (1f - wander) + independentLeft + continuousLeft + eventA.outLeft + eventB.outLeft
        val right = bed * (1f + wander) + independentRight + continuousRight + eventA.outRight + eventB.outRight
        previousNoise = noise
        sampleLeft = left * startupGain
        sampleRight = right * startupGain
    }

    internal fun storeVisualSignals(
        destination: PackedVisualTelemetry,
        startIndex: Int,
        generatorWeight: Float,
    ) {
        val weightScale = generatorWeight.coerceIn(0f, 1f)
        destination.store(
            startIndex,
            textureVisual.sound,
            textureVisual.weight * weightScale,
            textureVisual.convergence,
            textureVisual.energy,
        )
        destination.store(
            startIndex + 1,
            droneVisual.sound,
            droneVisual.weight * weightScale,
            droneVisual.convergence,
            droneVisual.energy,
        )
        destination.store(
            startIndex + 2,
            toneVisual.sound,
            toneVisual.weight * weightScale,
            toneVisual.convergence,
            toneVisual.energy,
        )
        destination.store(
            startIndex + 3,
            eventAVisual.sound,
            eventAVisual.weight * weightScale,
            eventAVisual.convergence,
            eventAVisual.energy,
        )
        destination.store(
            startIndex + 4,
            eventBVisual.sound,
            eventBVisual.weight * weightScale,
            eventBVisual.convergence,
            eventBVisual.energy,
        )
    }

    private fun nextTextureSample(noise: Float, motion: Float): Float {
        val high = noise - previousNoise
        return when (profile.texture) {
            TextureKind.Wind -> commonSlow * (0.45f + motion * 0.7f) + commonDeep * 0.7f
            TextureKind.Rain -> high * 0.62f + commonFast * 0.42f + commonSlow * motion * 0.25f
            TextureKind.Surf -> {
                val swell = motion * motion
                commonSlow * (0.35f + swell * 1.4f) + high * swell * 0.16f
            }
            TextureKind.Forest -> commonSlow * 0.45f + high * (0.12f + motion * 0.28f)
            TextureKind.Fire -> fireTexture.nextSample(
                noise,
                high,
                commonFast,
                commonSlow,
                commonDeep,
                motion,
            )
            TextureKind.Water -> commonFast * 0.47f + commonSlow * (0.32f + motion * 0.38f)
            TextureKind.Train -> commonDeep * 1.15f + commonFast * (0.22f + motion * 0.18f)
            TextureKind.Steam -> high * (0.34f + motion * 0.36f) + commonDeep * 0.55f
            TextureKind.Room -> commonSlow * 0.42f + commonDeep * 0.62f
            TextureKind.Brook,
            TextureKind.Waterfall,
            TextureKind.Blizzard,
            TextureKind.Ventilation,
            TextureKind.DistantTraffic,
            TextureKind.BrownNoise,
            TextureKind.BoatEngine,
            -> additionalTexture.nextSample(noise, motion)
        }
    }

    private fun nextContinuousTone() {
        var signal = 0f
        var analogLeft = 0f
        var analogRight = 0f
        lastTonePulse = 0f
        if (profile.droneLevel > 0f && profile.droneFrequency > 0.0) {
            droneImperfection.nextFrame()
            if (oscillatorControlCounter == 0) {
                droneOscillator.setFrequency(
                    profile.droneFrequency * droneImperfection.pitchMultiplier,
                )
                droneOvertoneOscillator.setFrequency(
                    profile.droneFrequency * 2.01 * droneImperfection.pitchMultiplier *
                        droneImperfection.secondaryPitchMultiplier,
                )
            }
            val fundamental = droneOscillator.next()
            val overtone = droneOvertoneOscillator.next()
            signal += (fundamental + overtone * 0.28f) * profile.droneLevel *
                droneImperfection.amplitudeMultiplier
            analogLeft += droneImperfection.noiseLeft * profile.droneLevel *
                AnalogImperfectionState.NOISE_MIX
            analogRight += droneImperfection.noiseRight * profile.droneLevel *
                AnalogImperfectionState.NOISE_MIX
            if (visualControlCounter == 0) {
                val beatPhase = droneOvertoneOscillator.phaseRadians -
                    droneOscillator.phaseRadians * 2.0
                lastDroneMotion = 0.72f + 0.28f * abs(sin(beatPhase).toFloat())
            }
        }
        if (profile.toneLevel > 0f && profile.toneFrequency > 0.0) {
            toneImperfection.nextFrame()
            if (oscillatorControlCounter == 0) {
                toneOscillator.setFrequency(
                    profile.toneFrequency * toneImperfection.pitchMultiplier,
                )
            }
            val pulse = 0.22f + 0.78f * max(0f, tonePulseControl.next())
            lastTonePulse = pulse
            signal += toneOscillator.next() * profile.toneLevel * pulse *
                toneImperfection.amplitudeMultiplier
            analogLeft += toneImperfection.noiseLeft * profile.toneLevel * pulse *
                AnalogImperfectionState.NOISE_MIX
            analogRight += toneImperfection.noiseRight * profile.toneLevel * pulse *
                AnalogImperfectionState.NOISE_MIX
        }
        sampleLeft = signal * 0.98f + analogLeft
        sampleRight = signal + analogRight
    }

    private fun updateVisualFrame(motion: Float, textureSample: Float) {
        val instantaneous = min(abs(textureSample) * 1.8f, 1f)
        val textureCoefficient = if (instantaneous > textureVisualEnergy) {
            visualAttackCoefficient
        } else {
            visualReleaseCoefficient
        }
        textureVisualEnergy += textureCoefficient * (instantaneous - textureVisualEnergy)

        val approaching = motion >= previousVisualMotion
        previousVisualMotion = motion
        if (profile.texture == TextureKind.Surf) {
            val target = if (approaching) motion * motion else 0f
            val surfCoefficient = if (target > surfVisualConvergence) {
                visualAttackCoefficient
            } else {
                visualReleaseCoefficient
            }
            surfVisualConvergence += surfCoefficient * (target - surfVisualConvergence)
        } else {
            surfVisualConvergence = 0f
        }

        val textureConvergence = when (profile.texture) {
            TextureKind.Surf -> surfVisualConvergence
            TextureKind.Rain -> min(0.28f + textureVisualEnergy * 0.48f + motion * 0.12f, 1f)
            TextureKind.Wind -> 0.18f + motion * 0.38f
            TextureKind.Forest -> 0.22f + motion * 0.28f
            TextureKind.Fire -> min(0.34f + textureVisualEnergy * 0.62f, 1f)
            TextureKind.Water -> 0.30f + motion * 0.42f
            TextureKind.Train -> 0.62f + motion * 0.28f
            TextureKind.Steam -> 0.20f + motion * 0.50f
            TextureKind.Room -> 0.52f + motion * 0.12f
            TextureKind.Brook -> 0.28f + textureVisualEnergy * 0.42f
            TextureKind.Waterfall -> 0.48f + textureVisualEnergy * 0.40f
            TextureKind.Blizzard -> 0.15f + motion * 0.55f
            TextureKind.Ventilation -> 0.62f + motion * 0.12f
            TextureKind.DistantTraffic -> 0.22f + textureVisualEnergy * 0.58f
            TextureKind.BrownNoise -> 0.24f + textureVisualEnergy * 0.20f
            TextureKind.BoatEngine -> 0.58f + textureVisualEnergy * 0.30f
        }
        val textureEnergy = min(0.30f + textureVisualEnergy * 0.52f + motion * 0.18f, 1f)
        val droneStrength = min(profile.droneLevel / 0.034f, 1f)
        val toneStrength = min(profile.toneLevel / 0.016f, 1f) * lastTonePulse
        val toneSound = when {
            profile.toneFrequency < 250.0 -> AmbienceVisualSound.ToneLow
            profile.toneFrequency < 2_000.0 -> AmbienceVisualSound.ToneMid
            else -> AmbienceVisualSound.ToneHigh
        }
        val audibleGain = startupGain.coerceIn(0f, 1f)

        textureVisual.sound = profile.texture.visualSound
        textureVisual.weight = (0.52f + textureEnergy * 0.20f) * audibleGain
        textureVisual.convergence = textureConvergence.coerceIn(0f, 1f)
        textureVisual.energy = textureEnergy * audibleGain
        droneVisual.sound = AmbienceVisualSound.Drone
        droneVisual.weight = droneStrength * 0.24f * audibleGain
        droneVisual.convergence = 0.72f + lastDroneMotion * 0.18f
        droneVisual.energy = droneStrength * lastDroneMotion * audibleGain
        toneVisual.sound = toneSound
        toneVisual.weight = toneStrength * 0.30f * audibleGain
        toneVisual.convergence = 0.52f + lastTonePulse * 0.44f
        toneVisual.energy = toneStrength * audibleGain
        eventAVisual.sound = eventA.visualSound
        eventAVisual.weight = eventA.visualActivity * 0.92f * audibleGain
        eventAVisual.convergence = eventA.visualConvergence
        eventAVisual.energy = min(eventA.visualActivity * 1.15f, 1f) * audibleGain
        eventBVisual.sound = eventB.visualSound
        eventBVisual.weight = eventB.visualActivity * 0.92f * audibleGain
        eventBVisual.convergence = eventB.visualConvergence
        eventBVisual.energy = min(eventB.visualActivity * 1.15f, 1f) * audibleGain
    }

    private fun softLimit(sample: Float): Float = sample / (1f + abs(sample))

    private companion object {
        const val OSCILLATOR_CONTROL_INTERVAL_FRAMES = 32
    }
}
